import React, { useState, useEffect } from 'react';
import { Form, Button, Nav } from 'react-bootstrap';
import UkiesAssets from '../Admin/UkiesAssets';

export default function UkiesPhotoGallery({ componentSettings, setComponentSettings, onChange, selectedComponent, setSelectedComponent }) {
    const [activeSlideTab, setActiveSlideTab] = useState(0);
    const [showAssetSelector, setShowAssetSelector] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [draggedTab, setDraggedTab] = useState(null);
    const [dragOverTab, setDragOverTab] = useState(null);
    const apiToken = process.env.REACT_APP_UKIES_API_TOKEN;

    // Get current values with defaults
    const format = componentSettings.format || 'normal';
    const height = componentSettings.height || 600;
    const autoplay = componentSettings.autoplay !== undefined ? componentSettings.autoplay : true;
    const interval = componentSettings.interval || '8000';
    const showArrows = componentSettings.show_arrows !== undefined ? componentSettings.show_arrows : false;
    const showNavigation = componentSettings.show_navigation !== undefined ? componentSettings.show_navigation : true;
    const slides = Array.isArray(componentSettings.slides) ? componentSettings.slides : [];

    // Stable splide id per component, so multiple galleries on one page never collide
    const splideId = `splide_gallery_${selectedComponent?.id || 'new'}`;

    // Parse HTML content to extract gallery configuration and slides
    const parseHTMLContent = (htmlContent) => {
        if (!htmlContent) return null;

        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlContent, 'text/html');

            const mainDiv = doc.querySelector('.ukies-photo-gallery');
            if (!mainDiv) return null;

            const config = {
                format: mainDiv.classList.contains('structure-fullwidth') ? 'fullwidth' : 'normal',
                height: 600,
                autoplay: true,
                interval: '8000',
                show_arrows: false,
                show_navigation: true,
                slides: []
            };

            // Extract height from the slide list style
            const list = doc.querySelector('.splide__list');
            if (list) {
                const listStyle = list.getAttribute('style') || '';
                const heightMatch = listStyle.match(/height:\s*(\d+)px/);
                if (heightMatch) {
                    config.height = parseInt(heightMatch[1]);
                }
            }

            // Extract configuration from the mount script
            const scriptTag = doc.querySelector('script');
            if (scriptTag) {
                const scriptContent = scriptTag.textContent;

                if (scriptContent.includes("autoplay: 'play'") || scriptContent.includes('autoplay: true')) {
                    config.autoplay = true;
                } else if (scriptContent.includes('autoplay: false')) {
                    config.autoplay = false;
                }

                const intervalMatch = scriptContent.match(/interval:\s*(\d+)/);
                if (intervalMatch) {
                    config.interval = intervalMatch[1];
                }

                const arrowsMatch = scriptContent.match(/arrows:\s*(true|false)/);
                if (arrowsMatch) {
                    config.show_arrows = arrowsMatch[1] === 'true';
                }

                const paginationMatch = scriptContent.match(/pagination:\s*(true|false)/);
                if (paginationMatch) {
                    config.show_navigation = paginationMatch[1] === 'true';
                }
            }

            // Extract slides
            const slideElements = doc.querySelectorAll('.splide__slide');
            config.slides = Array.from(slideElements).map(slide => {
                const style = slide.getAttribute('style') || '';

                const bgImageMatch = style.match(/background-image:\s*url\(([^)]+)\)/);
                const background_image = bgImageMatch ? bgImageMatch[1].replace(/['"]/g, '') : '';

                const captionEl = slide.querySelector('.slide-body');
                const caption = captionEl ? captionEl.textContent.trim() : '';

                return { background_image, caption };
            });

            return config;
        } catch (error) {
            console.error('Error parsing HTML content:', error);
            return null;
        }
    };

    // HTML escaping function to prevent XSS
    const escapeHtml = (text) => {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    };

    // Generate HTML content from provided settings object
    const generateHTMLContentFromSettings = (settings) => {
        const settingsSlides = Array.isArray(settings.slides) ? settings.slides : [];
        const settingsHeight = settings.height || 600;
        const settingsAutoplay = settings.autoplay !== undefined ? settings.autoplay : true;
        const settingsInterval = settings.interval || '8000';
        const settingsShowArrows = settings.show_arrows !== undefined ? settings.show_arrows : false;
        const settingsShowNavigation = settings.show_navigation !== undefined ? settings.show_navigation : true;
        const settingsFormat = settings.format || 'normal';

        const baseStructureClass = settingsFormat === 'fullwidth' ? 'structure-fullwidth' : 'structure structure-1';
        const autoplayValue = settingsAutoplay ? "'play'" : "false";
        const arrowsValue = settingsShowArrows ? "true" : "false";
        const paginationValue = settingsShowNavigation ? "true" : "false";

        const slidesHTML = settingsSlides.map((slide, index) => {
            const escapedCaption = escapeHtml(slide.caption || '');

            return `              <li class="component splide__slide" style="background-image:url(${slide.background_image || ''});display:flex;align-items:end;">
                <div id='${splideId}--slide-${index + 1}' class='overlay-container' style="min-height:100px;height:unset;padding-top:10px;">
                    <div class='slide-container' style="justify-content:flex-end;padding: 0 0 3rem 0">
                        <div class='slide-description text-shadow'>
                            <div class='slide-body'>
                                ${escapedCaption}
                            </div>
                        </div>
                    </div>
                </div>
              </li>`;
        }).join('\n');

        return `<div class='${baseStructureClass} ukies-photo-gallery'>
    <section id="${splideId}" class="splide">
        <div class="splide__track">
              <ul class="splide__list" style='height:${settingsHeight}px;'>
${slidesHTML}
              </ul>
        </div>
        <button class="splide__toggle" type="button">
            <svg
              class="splide__toggle__play"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="m22 12-20 11v-22l10 5.5z"/>
            </svg>
            <svg
              class="splide__toggle__pause"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="m2 1v22h7v-22zm13 0v22h7v-22z"/>
            </svg>
        </button>
    </section>
    <script>
        // Set ID for splide
        let id ='${splideId}';

        // Set variables for splide element(s)
        let element = document.getElementById(id);

        // Check if not already mounted
        if(!element.classList.contains('is-initialized')) {
            // Configure splide
            let splide = new Splide( '#'+element.id, {
                type: 'loop',
                pagination: ${paginationValue},
                arrows: ${arrowsValue},
                autoplay: ${autoplayValue},
                interval: ${settingsInterval},
                pauseOnHover: true,
                pauseOnFocus: true,
                perPage: 1
            });

            // Mount splide
            splide.mount();
        } else {
            // Log
            console.log('Splide ' + id + ' was already mounted.');
        }
    </script>
</div>`;
    };

    // Initialize form values from HTML content on component mount
    useEffect(() => {
        if (selectedComponent?.content && !isInitialized) {
            const parsed = parseHTMLContent(selectedComponent.content);
            if (parsed) {
                setComponentSettings(prev => ({
                    ...prev,
                    format: parsed.format,
                    height: parsed.height,
                    autoplay: parsed.autoplay,
                    interval: parsed.interval,
                    show_arrows: parsed.show_arrows,
                    show_navigation: parsed.show_navigation,
                    slides: parsed.slides
                }));
            }
            setIsInitialized(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedComponent?.content, isInitialized, setComponentSettings]);

    // Reset initialization when component changes
    useEffect(() => {
        setIsInitialized(false);
    }, [selectedComponent?.id]);

    // Handle simple field changes and update HTML content
    const handleFieldChange = (fieldKey, value) => {
        const updatedSettings = { ...componentSettings, [fieldKey]: value };
        const newContent = generateHTMLContentFromSettings(updatedSettings);

        setComponentSettings(prev => ({
            ...prev,
            [fieldKey]: value,
            content: newContent
        }));

        onChange(fieldKey, value);
        onChange('content', newContent);
    };

    // Handle slide array changes and update HTML content
    const handleSlidesChange = (newSlides) => {
        const updatedSettings = { ...componentSettings, slides: newSlides };
        const newContent = generateHTMLContentFromSettings(updatedSettings);

        setComponentSettings(prev => ({
            ...prev,
            slides: newSlides,
            content: newContent
        }));

        onChange('slides', newSlides);
        onChange('content', newContent);
    };

    // Add new slide
    const handleAddSlide = () => {
        const newSlide = {
            background_image: '',
            caption: ''
        };
        const newSlides = [...slides, newSlide];
        handleSlidesChange(newSlides);
        setActiveSlideTab(newSlides.length - 1);
    };

    // Remove slide
    const handleRemoveSlide = (index) => {
        const newSlides = slides.filter((_, i) => i !== index);
        handleSlidesChange(newSlides);
        if (activeSlideTab >= newSlides.length && newSlides.length > 0) {
            setActiveSlideTab(newSlides.length - 1);
        } else if (newSlides.length === 0) {
            setActiveSlideTab(0);
        }
    };

    // Move slide left
    const handleMoveSlideUp = (index) => {
        if (index === 0) return;
        const newSlides = [...slides];
        [newSlides[index - 1], newSlides[index]] = [newSlides[index], newSlides[index - 1]];
        handleSlidesChange(newSlides);
        setActiveSlideTab(index - 1);
    };

    // Move slide right
    const handleMoveSlideDown = (index) => {
        if (index === slides.length - 1) return;
        const newSlides = [...slides];
        [newSlides[index], newSlides[index + 1]] = [newSlides[index + 1], newSlides[index]];
        handleSlidesChange(newSlides);
        setActiveSlideTab(index + 1);
    };

    // Handle slide field changes
    const handleSlideFieldChange = (slideIndex, fieldKey, value) => {
        const newSlides = [...slides];
        newSlides[slideIndex] = {
            ...newSlides[slideIndex],
            [fieldKey]: value
        };
        handleSlidesChange(newSlides);
    };

    // Get small preview image for a slide tab
    const getSlideTabImage = (slide) => {
        if (!slide?.background_image) return null;
        return slide.background_image.replace('/max', '/medium');
    };

    // Drag and drop handlers for tabs
    const handleTabDragStart = (e, index) => {
        setDraggedTab(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', e.target.outerHTML);

        setTimeout(() => {
            e.target.style.opacity = '0.5';
        }, 0);
    };

    const handleTabDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverTab(index);
    };

    const handleTabDragLeave = () => {
        setDragOverTab(null);
    };

    const handleTabDrop = (e, dropIndex) => {
        e.preventDefault();

        if (draggedTab === null || draggedTab === dropIndex) {
            setDraggedTab(null);
            setDragOverTab(null);
            return;
        }

        const newSlides = [...slides];
        const [movedSlide] = newSlides.splice(draggedTab, 1);
        newSlides.splice(dropIndex, 0, movedSlide);

        handleSlidesChange(newSlides);

        if (activeSlideTab === draggedTab) {
            setActiveSlideTab(dropIndex);
        } else if (activeSlideTab > draggedTab && activeSlideTab <= dropIndex) {
            setActiveSlideTab(activeSlideTab - 1);
        } else if (activeSlideTab < draggedTab && activeSlideTab >= dropIndex) {
            setActiveSlideTab(activeSlideTab + 1);
        }

        setDraggedTab(null);
        setDragOverTab(null);
    };

    const handleTabDragEnd = (e) => {
        e.target.style.opacity = '1';
        setDraggedTab(null);
        setDragOverTab(null);
    };

    // Handle asset selection for background image
    const handleAssetSelect = (asset) => {
        const imageUrl = asset.image_variations?.large
            ? asset.image_variations.large.replace('/large', '/max')
            : asset.url;

        handleSlideFieldChange(activeSlideTab, 'background_image', imageUrl);
        setShowAssetSelector(false);
    };

    return (
        <div>
            <style jsx>{`
                .add-button-green {
                    background-color: var(--ukies-light-green) !important;
                    border-color: var(--ukies-light-green) !important;
                    color: white !important;
                }
                .add-button-green:hover:not(:disabled) {
                    background-color: var(--ukies-dark-green) !important;
                    border-color: var(--ukies-dark-green) !important;
                    color: white !important;
                }
                .add-button-green:focus:not(:disabled) {
                    background-color: var(--ukies-dark-green) !important;
                    border-color: var(--ukies-dark-green) !important;
                    color: white !important;
                    box-shadow: 0 0 0 0.2rem rgba(59, 79, 70, 0.25) !important;
                }
                .add-button-green:active:not(:disabled) {
                    background-color: var(--ukies-dark-green) !important;
                    border-color: var(--ukies-dark-green) !important;
                    color: white !important;
                }

                .nav-link.dragging {
                    opacity: 0.5 !important;
                    transform: rotate(2deg) !important;
                    transition: transform 0.1s ease-in-out !important;
                }

                .nav-link.drag-over {
                    border-color: var(--ukies-dark-orange) !important;
                    border-width: 2px !important;
                    transform: scale(1.02) !important;
                    transition: all 0.1s ease-in-out !important;
                    box-shadow: 0 0 8px rgba(0, 0, 0, 0.2) !important;
                }

                .nav-link[draggable="true"]:hover {
                    cursor: grab !important;
                }

                .nav-link[draggable="true"]:active {
                    cursor: grabbing !important;
                }
            `}</style>

            {/* Gallery Configuration Header */}
            <h6 className="mb-3 mt-3">
                Fotogalerij
            </h6>

            {/* Format Selection */}
            <Form.Group className="mb-3">
                <Form.Label>Formaat</Form.Label>
                <Form.Select
                    value={format}
                    onChange={(e) => handleFieldChange('format', e.target.value)}
                >
                    <option value="normal">Normaal</option>
                    <option value="fullwidth">Volle breedte</option>
                </Form.Select>
            </Form.Group>

            {/* Height */}
            <Form.Group className="mb-3">
                <Form.Label>Hoogte (px)</Form.Label>
                <Form.Control
                    type="number"
                    value={height}
                    onChange={(e) => handleFieldChange('height', parseInt(e.target.value) || 0)}
                    min="100"
                    step="10"
                />
            </Form.Group>

            {/* Autoplay Switch */}
            <Form.Group className="mb-1">
                <Form.Check
                    type="switch"
                    id="autoplay-switch"
                    label="Automatisch"
                    checked={autoplay}
                    onChange={(e) => handleFieldChange('autoplay', e.target.checked)}
                />
            </Form.Group>

            {/* Interval Select - Only show when autoplay is enabled */}
            {autoplay && (
                <Form.Group className="mb-3">
                    <Form.Label>Tijd per slide</Form.Label>
                    <Form.Select
                        value={interval}
                        onChange={(e) => handleFieldChange('interval', e.target.value)}
                    >
                        <option value="1000">1 seconde</option>
                        <option value="2000">2 seconden</option>
                        <option value="3000">3 seconden</option>
                        <option value="4000">4 seconden</option>
                        <option value="5000">5 seconden</option>
                        <option value="6000">6 seconden</option>
                        <option value="7000">7 seconden</option>
                        <option value="8000">8 seconden</option>
                        <option value="9000">9 seconden</option>
                        <option value="10000">10 seconden</option>
                    </Form.Select>
                </Form.Group>
            )}

            {/* Show Arrows Switch */}
            <Form.Group className="mb-1">
                <Form.Check
                    type="switch"
                    id="show-arrows-switch"
                    label="Vorige/Volgende pijltjes"
                    checked={showArrows}
                    onChange={(e) => handleFieldChange('show_arrows', e.target.checked)}
                />
            </Form.Group>

            {/* Show Navigation Switch */}
            <Form.Group className="mb-4">
                <Form.Check
                    type="switch"
                    id="show-navigation-switch"
                    label="Navigatie (stipjes)"
                    checked={showNavigation}
                    onChange={(e) => handleFieldChange('show_navigation', e.target.checked)}
                />
            </Form.Group>

            {/* Slides Section */}
            <Form.Group className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">
                        Foto's
                    </h6>
                    <Button
                        variant="success"
                        size="sm"
                        onClick={handleAddSlide}
                        className="add-button-green"
                    >
                        + Foto toevoegen
                    </Button>
                </div>

                {slides.length === 0 ? (
                    <div className="text-center py-4 border rounded bg-light">
                        <p className="text-muted mb-2">Geen foto's toegevoegd</p>
                        <Button variant="primary" size="sm" onClick={handleAddSlide}>
                            Eerste foto toevoegen
                        </Button>
                    </div>
                ) : (
                    <div className="array-field-tabs">
                        {/* Slide Tabs */}
                        <Nav variant="tabs" className="gap-1">
                            {slides.map((slide, index) => (
                                <Nav.Item key={index} className="p-0">
                                    <Nav.Link
                                        active={activeSlideTab === index}
                                        onClick={() => {
                                            if (draggedTab === null) {
                                                setActiveSlideTab(index);
                                            }
                                        }}
                                        className={`d-flex align-items-center gap-2 p-1 ${
                                            draggedTab === index ? 'dragging' : ''
                                        } ${
                                            dragOverTab === index ? 'drag-over' : ''
                                        }`}
                                        draggable="true"
                                        onDragStart={(e) => handleTabDragStart(e, index)}
                                        onDragOver={(e) => handleTabDragOver(e, index)}
                                        onDragLeave={handleTabDragLeave}
                                        onDrop={(e) => handleTabDrop(e, index)}
                                        onDragEnd={handleTabDragEnd}
                                        style={{ cursor: 'grab' }}
                                        title="Sleep om de volgorde te wijzigen"
                                    >
                                        <div style={{ position: 'relative' }}>
                                            {getSlideTabImage(slide) ?
                                                <img
                                                    src={getSlideTabImage(slide)}
                                                    alt="Slide preview"
                                                    style={{
                                                        width: '120px',
                                                        height: '80px',
                                                        objectFit: 'cover',
                                                        borderRadius: '4px 4px 0px 0px',
                                                        opacity: activeSlideTab === index ? 1 : 0.5,
                                                        transition: 'opacity 0.2s ease-in-out',
                                                        pointerEvents: 'none'
                                                    }}
                                                />
                                                : <div style={{
                                                    width: '120px',
                                                    height: '80px',
                                                    backgroundColor: 'var(--ukies-light-green)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white',
                                                    fontWeight: 'bold',
                                                    borderRadius: '4px 4px 0px 0px',
                                                    pointerEvents: 'none'
                                                }}>
                                                    <span>Nieuwe foto</span>
                                                </div>
                                            }
                                        </div>
                                    </Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>

                        {/* Slide Content */}
                        <div className="tab-content-wrapper p-3">
                            {slides.length > 0 && slides[activeSlideTab] && (
                                <div>
                                    {/* Slide Management Buttons */}
                                    <div className="d-flex justify-content-between align-items-center mb-3 pb-2" style={{ borderBottom: '1px solid #dee2e6' }}>
                                        <h6 className="mb-0">
                                            Foto {activeSlideTab + 1} van {slides.length}
                                        </h6>
                                        <div className="d-flex gap-2">
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => handleMoveSlideUp(activeSlideTab)}
                                                disabled={activeSlideTab === 0}
                                                title="Verplaats naar links"
                                            >
                                                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                                                    <path d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/>
                                                </svg>
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => handleMoveSlideDown(activeSlideTab)}
                                                disabled={activeSlideTab === slides.length - 1}
                                                title="Verplaats naar rechts"
                                            >
                                                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                                                    <path d="m4.646 1.646 6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L9.293 8 3.646 2.354a.5.5 0 1 1 .708-.708z"/>
                                                </svg>
                                            </Button>
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => {
                                                    if (window.confirm('Weet je zeker dat je deze foto wilt verwijderen?')) {
                                                        handleRemoveSlide(activeSlideTab);
                                                    }
                                                }}
                                                title="Verwijder foto"
                                            >
                                                Verwijderen
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Background Image */}
                                    <Form.Group className="mb-3">
                                        <Form.Label>Afbeelding</Form.Label>
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() => setShowAssetSelector(true)}
                                                style={{
                                                    filter: 'grayscale(100%)',
                                                    minWidth: '35.2px',
                                                    height: '35.2px',
                                                    padding: '0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                🖼️
                                            </Button>
                                            <Form.Control
                                                type="text"
                                                value={slides[activeSlideTab]?.background_image || ''}
                                                onChange={(e) => handleSlideFieldChange(activeSlideTab, 'background_image', e.target.value)}
                                                placeholder="Voer afbeelding URL in"
                                                disabled
                                            />
                                        </div>
                                        {slides[activeSlideTab]?.background_image && (
                                            <div style={{ overflow: 'hidden', width: '100%', aspectRatio: '3/1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <img
                                                    src={slides[activeSlideTab].background_image}
                                                    alt="Achtergrond voorbeeld"
                                                    style={{
                                                        width: '100%',
                                                        objectFit: 'cover',
                                                        cursor: 'pointer',
                                                        border: '1px solid #dee2e6',
                                                        borderRadius: '4px'
                                                    }}
                                                    onClick={() => window.open(slides[activeSlideTab].background_image, '_blank')}
                                                />
                                            </div>
                                        )}
                                    </Form.Group>

                                    {/* Caption */}
                                    <Form.Group className="mb-3">
                                        <Form.Label>Bijschrift</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={slides[activeSlideTab]?.caption || ''}
                                            onChange={(e) => handleSlideFieldChange(activeSlideTab, 'caption', e.target.value)}
                                            placeholder="Voer bijschrift in"
                                        />
                                    </Form.Group>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Form.Group>

            {/* Asset Selector Dialog */}
            <UkiesAssets
                open={showAssetSelector}
                setOpen={setShowAssetSelector}
                onAssetSelect={handleAssetSelect}
                token={apiToken}
            />
        </div>
    );
}
